import {createRef, KeyboardEvent, PureComponent} from "react";

import Matrix from "./Matrix";
import Solution from "./Solution";
import PlayEditToggle, { PlayEdit } from "./PlayEditToggle"

import "./Game.css"
import Engine, { EditEngine, PlayEngine } from "./Enginge";
import { byRC, byValue } from "./rc";
import Progress from "./Progress";
import { char, dumpState, loadState, num, numbers } from "./serialization";

type GameProps = {};

export type GameState = {
  matrixData : Record<string, char>;
  solutionData: Record<string, char>;
  playEdit: PlayEdit;
}

export class Game extends PureComponent<GameProps, GameState> implements Engine {
  state : GameState = {
    matrixData: {},
    solutionData: Object.fromEntries(numbers.map(n => [n, ""])),
    playEdit: PlayEdit.Edit,
  };

  private myRefs = {
    matrix: createRef<Matrix>(),
    solution: createRef<Solution>(),
  }

  get matrix() : Matrix|null {
    return this.myRefs.matrix.current;
  }

  constructor(props: GameProps) {
    super(props);
    this.handlePlayEditChange = this.handlePlayEditChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  componentDidMount() {
    window.addEventListener("popstate", (e : PopStateEvent) => {
      loadState(this, e.state);
    });

    if (window.location.hash.length > 1) {
      loadState(this, {
        isEdit: false,
        stateWithVersion: window.location.hash.slice(1)
      });
    }
  }

  pushState() {
    this.componentDidUpdate = () => {
      this.componentDidUpdate = () => {}
      const s = dumpState(this);
      window.history.pushState(s, "", `#${s.stateWithVersion}`);
    }
  }

  get numberToLetter() : Record<num, char> {
    const sortUniq = Object.entries(this.state.matrixData)
      .sort(byRC)
      .sort(byValue)
      .filter(([,value], i, ary) => value !== "" && (i === 0 || ary[i-1][1] !== value))
      .sort(byRC)
      .map(([, value]) => value);

    let i = 0;
    let n2l = Object.fromEntries(numbers.map(n => [
      numbers[i], sortUniq[i++] || ""
    ]));

    return n2l as Record<num, char>;
  }

  get letterToNumber() : Record<string, string> {
    return Object.fromEntries(
      Object
        .entries(this.numberToLetter)
        .filter(([n, l]) => n !== "" && l !== "")
        .map(e => e.reverse())
    );
  }

  get engine() {
    switch (this.state.playEdit) {
      case PlayEdit.Play:
        return PlayEngine;
      case PlayEdit.Edit:
        return EditEngine;
      default:
        throw new Error("bad state");
    }
  }

  get matrixData() {
    return this.engine.matrixData.bind(this);
  }

  get handleMatrixDataChange() {
    return this.engine.handleMatrixDataChange.bind(this);
  }

  get solutionData() {
    return this.engine.solutionData.bind(this);
  }

  get handleSolutionDataChange() {
    return this.engine.handleSolutionDataChange.bind(this);
  }

  get progressData() {
    return this.engine.progressData.bind(this);
  }

  handlePlayEditChange(playEdit : PlayEdit) {
    this.setState({playEdit: playEdit});
  }

  handleKeyDown(event : KeyboardEvent<HTMLDivElement>) {
    // FF navigates backwards on backspace
    if (event.key === "Backspace") event.preventDefault();

    if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === "z")
      window.history.forward();
    else if ((event.ctrlKey || event.metaKey) && event.key === "z")
      window.history.back();

  }

  render() : JSX.Element {
    return (
      <div className="Game" onKeyDown={this.handleKeyDown}>
        <Matrix
          data={this.matrixData()}
          onChange={this.handleMatrixDataChange}
          ref={this.myRefs.matrix}
        />

        <Solution
          data={this.solutionData()}
          onChange={this.handleSolutionDataChange}
          ref={this.myRefs.solution}
        />

        <Progress data={this.progressData()} />

        <PlayEditToggle playEdit={this.state.playEdit} onChange={this.handlePlayEditChange} />
      </div>
    );
  }
}

export default Game;